"use client"

import { useState, useRef, useEffect } from "react"
import { Button } from "./button"
import { Textarea } from "./textarea"
import { toast } from "sonner"
import { post } from "../../lib/api"
import { useSidebar } from "./sidebar"

const FeedbackSlide = () => {
    const [isOpen, setIsOpen] = useState(false)
    const [feedback, setFeedback] = useState("")
    const [isSubmitting, setIsSubmitting] = useState(false)
    const textareaRef = useRef(null)
    const { user } = useSidebar()
    const [email, setEmail] = useState("")
    const pageUrl = typeof window !== "undefined" ? window.location.href : ""

    // Focus textarea when opened
    useEffect(() => {
        if (isOpen && textareaRef.current) {
            textareaRef.current.focus()
        }
    }, [isOpen])

    const handleSubmit = async () => {
        if (!feedback.trim()) {
            toast.error("Please enter some feedback before submitting.")
            return
        }
        // Simple email validation if provided
        if (email && !/^\S+@\S+\.\S+$/.test(email)) {
            toast.error("Please enter a valid email address.")
            return
        }
        setIsSubmitting(true)
        try {
            const response = await post(
                {
                    method: "submitFeedback",
                    feedback: feedback.trim(),
                    email: email.trim() || undefined,
                    pageUrl: pageUrl,
                    timestamp: new Date().toISOString(),
                    name: user && user[1] ? user[1] : "Anonymous",
                },
                "/administration"
            )
            if (response.error) {
                toast.error("Failed to submit feedback. Please try again.")
                return
            }
            toast.success(
                email.trim()
                    ? "Thank you for your feedback! We'll notify you when it's fixed. 💖"
                    : "Thank you for your feedback! 💖"
            )
            setFeedback("")
            setEmail("")
            setIsOpen(false)
        } catch (err) {
            toast.error("Failed to submit feedback. Please try again.")
            console.error(err)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setIsOpen(false)
    }

    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            handleClose()
        } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
            handleSubmit()
        }
    }

    return (
        <>
            {/* Floating Feedback Tab - Slides down when open */}
            <div className={`fixed bottom-0 right-6 z-[9999] transition-all duration-300 ease-in-out ${
                isOpen ? "translate-y-full opacity-0 pointer-events-none" : "translate-y-0 opacity-100"
            }`}>
                <Button
                    onClick={() => setIsOpen(true)}
                    className="bg-purple-600 hover:bg-purple-700 dark:bg-purple-700 dark:hover:bg-purple-800 text-white px-3 py-2 font-semibold shadow-md rounded-t-xl rounded-b-none transition-all duration-200 hover:scale-105"
                    aria-label="Send Feedback"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8 9h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M8 13h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Feedback
                </Button>
            </div>

            {/* Slide-up Feedback Panel */}
            <div 
                className={`fixed bottom-0 right-6 z-[9998] transition-all duration-300 ease-in-out ${
                    isOpen 
                        ? "translate-y-0 opacity-100" 
                        : "translate-y-full opacity-0 pointer-events-none"
                }`}
            >
                <div className="bg-[#ffffff] dark:bg-[#2c313c] rounded-t-xl shadow-lg p-6 w-96 max-w-[90vw]">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M8 9h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M8 13h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Feedback Form
                        </h3>
                    </div>

                    {/* Feedback Input */}
                    <div className="space-y-3">

                        <Textarea
                            ref={textareaRef}
                            id="feedback"
                            placeholder="Tell us what you think, report a problem, or suggest a feature..."
                            value={feedback}
                            onChange={(e) => setFeedback(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="min-h-[120px] text-gray-900 dark:text-white dark:bg-gray-800 dark:placeholder-gray-500 dark:border-gray-600 placeholder-gray-500 border border-gray-300 dark:border-gray-600 resize-none shadow-none"
                            maxLength={1000}
                        />
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex justify-between pb-2">
                            <span>Press Ctrl+Enter to submit</span>
                            <span>{feedback.length}/1000</span>
                        </div>
                        <input
                            type="email"
                            id="email"
                            placeholder="Your email (optional)"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 mt-2"
                            maxLength={100}
                            autoComplete="email"
                            disabled={isSubmitting}
                        />
                        <div className="text-xs text-gray-500 dark:text-gray-400 pt-1">
                            If you provide your email, we'll notify you when your issue or feature request is addressed.
                        </div>
                    </div>

                    {/* Info Text */}
                    <div className="p-3 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-900 rounded-lg mb-4">
                        <div className="text-sm text-gray-800 dark:text-white">
                            <strong>Important:</strong> Please provide the page and section where the issue occurred. This will help us fix the issue faster.
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                        <Button
                            className="flex-1 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800 text-white border border-green-700 dark:border-green-700 py-2 font-semibold shadow-md"
                            onClick={handleSubmit}
                            disabled={isSubmitting || !feedback.trim()}
                        >
                            {isSubmitting ? "Sending..." : "Send Feedback"}
                        </Button>
                        <Button
                            variant="outline"
                            className="border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-white px-4 py-2 font-medium shadow-sm"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            </div>

            {/* Click outside to close - invisible overlay */}
            {isOpen && (
                <div 
                    className="fixed inset-0 z-[9997]"
                    onClick={handleClose}
                />
            )}
        </>
    )
}

export default FeedbackSlide 